import React, { useEffect, useRef, useState } from 'react';
import { geocodeByAddress, geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';

import AddressAutocompleteBase from '../misc/AddressAutocomplete';
import {
    buildCanonicalAddress,
    buildAutocompleteAddressUpdate,
    extractAddressComponents,
    streetFromSuggestion,
} from '@/utilities/address';
import { selectBestResult, isNonUsResult } from '@/utilities/selectBestResult';

export default function AddressAutocomplete(props) {
    const { state, handleAddressUpdate, onBlur, error, helperText, disableSearch = false, label = 'Address' } = props;

    const [addressEntry, setAddressEntry] = useState([]);
    const [focused, setFocused] = useState(false);

    // Blur/select race
    const lastInputActionRef = useRef(null);

    const handleAddressEntry = (entry) => {
        lastInputActionRef.current = 'type';
        setAddressEntry((prev) => [entry, prev[1]]);
    };

    const focus = () => setFocused(true);

    const blur = async () => {
        if (onBlur) onBlur();
        setFocused(false);

        if (lastInputActionRef.current === 'select') {
            // Selection owns structured write
            lastInputActionRef.current = null;
            return;
        }

        if (!addressEntry[0]) {
            const empty = {
                city: state.city || '',
                state: state.state || '',
                zip: state.zip || '',
                street: '',
            };
            setFocused(false);
            setAddressEntry((prev) => [prev[0], empty]);
            handleAddressUpdate('', empty);
            return;
        }

        // Dropdown-compatible merge
        const typed = addressEntry[0];
        // Street-only fallback
        const fallbackStreet = streetFromSuggestion(undefined, typed);
        const lookupAddress = buildCanonicalAddress({
            street: fallbackStreet,
            city: state.city,
            state: state.state,
            zip: state.zip,
        });

        try {
            const results = await geocodeByAddress(lookupAddress || typed);
            const best = selectBestResult(results);
            if (best && !isNonUsResult(best)) {
                const extracted = extractAddressComponents(best.address_components || []);

                let lat;
                let lng;
                if (extracted.hasRoute) {
                    try {
                        ({ lat, lng } = await getLatLng(best));
                    } catch (err) {
                        console.warn('getLatLng failed for typed address:', err);
                    }
                }

                const next = buildAutocompleteAddressUpdate({
                    extracted,
                    state,
                    fallbackStreet,
                    address: typed,
                    formattedAddress: best.formatted_address,
                    lat,
                    lng,
                    partialMatch: !!best.partial_match,
                    locationType: best.geometry?.location_type || '',
                });

                const addressLine = next.addressLine || fallbackStreet;
                setAddressEntry([addressLine, next]);
                handleAddressUpdate(addressLine, next);
                return;
            }
        } catch (err) {
            console.warn('Geocode-on-blur failed for typed address:', err);
        }

        // Preserve typed line 1
        const fallbackFullAddress = [fallbackStreet, state.city, state.state, state.zip].filter((x) => !!x).join(', ');
        const next = {
            city: state.city || '',
            state: state.state || '',
            zip: state.zip || '',
            street: fallbackStreet,
            addressLine: fallbackStreet,
            address: fallbackFullAddress,
            fullAddress: fallbackFullAddress,
            resolved: false,
        };

        setAddressEntry([fallbackStreet, next]);
        handleAddressUpdate(fallbackStreet, next);
    };

    const selectAddress = async (address, placeId, suggestion) => {
        lastInputActionRef.current = 'select';
        // Street-only fallback
        const fallbackStreet = streetFromSuggestion(suggestion, address);

        try {
            const results = await geocodeByPlaceId(placeId);
            const place = results[0];
            const components = place?.address_components || [];

            const extracted = extractAddressComponents(components);

            // Skip coarse lat/lng
            let lat;
            let lng;
            if (extracted.hasRoute) {
                try {
                    ({ lat, lng } = await getLatLng(place));
                } catch (err) {
                    console.warn('getLatLng failed for selected place:', err);
                }
            }

            const next = buildAutocompleteAddressUpdate({
                extracted,
                state,
                fallbackStreet,
                address,
                formattedAddress: place?.formatted_address,
                lat,
                lng,
                partialMatch: !!place?.partial_match,
                locationType: place?.geometry?.location_type || '',
            });

            const addressLine = next.addressLine || fallbackStreet;
            setAddressEntry([addressLine, next]);
            handleAddressUpdate(addressLine, next);
        } catch (error) {
            console.error('Error fetching place details:', error);
            // Preserve line 1 + context
            const fallbackFullAddress = [fallbackStreet, state?.city, state?.state, state?.zip]
                .filter((x) => !!x)
                .join(', ');
            const next = {
                city: state?.city || '',
                state: state?.state || '',
                zip: state?.zip || '',
                street: fallbackStreet,
                addressLine: fallbackStreet,
                address: fallbackFullAddress,
                fullAddress: fallbackFullAddress,
                resolved: false,
            };
            setAddressEntry([fallbackStreet, next]);
            handleAddressUpdate(fallbackStreet, next);
        }
    };

    useEffect(() => {
        if (state.street !== addressEntry[1]?.street || state.street !== addressEntry[0]) {
            setAddressEntry([
                state.street,
                {
                    city: state.city,
                    state: state.state,
                    zip: state.zip,
                    street: state.street,
                },
            ]);
        }
    }, [state.street]);

    return (
        <AddressAutocompleteBase
            disableSearch={disableSearch}
            disabled={props.disabled}
            label={label}
            showLabel
            value={addressEntry[0] || ''}
            onInput={handleAddressEntry}
            onSelect={selectAddress}
            onBlur={blur}
            onFocus={focus}
            open={focused}
            error={error}
            helperText={helperText}
            size="medium"
        />
    );
}
